import React from 'react';
import {
  graphql,
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay';
import styled from 'styled-components';
import { getCurrentAccount } from '../../account_helpers';
import { makeMutation } from '../../api/helpers';
import { OnboardMerchant, UpdateMerchantPayments } from '../../api/mutations';
import { mutationsUpdateMerchantMutationResponse } from '../../api/__generated__/mutationsUpdateMerchantMutation.graphql';
import { HelpText } from '../../components/HelpText';
import { InfoLabel } from '../../components/InfoLabel';
import FlatButton from '../../components/input/FlatButton';
import { PageSection } from '../../components/PageSection';
import Spacer from '../../components/Spacer';
import { useToastDispatch } from '../../components/Toast/ToastProvider';
import { PageTitle } from '../../components/typography/Typography';
import type { MerchantBilling_Query as MerchantBilling_QueryType } from './__generated__/MerchantBilling_Query.graphql';
import MerchantBilling_Query from './__generated__/MerchantBilling_Query.graphql';
import moment from 'moment';
import RowLabel from '../../components/RowLabel';

const Container = styled.div`
  flex: 1;
`;

const BalanceSection = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 434px;
`;

type LoaderProps = {
  queryRef: PreloadedQuery<MerchantBilling_QueryType>;
};

export default function BillingLoader({ queryRef }: LoaderProps) {
  const [qRef] = useQueryLoader<MerchantBilling_QueryType>(
    MerchantBilling_Query,
    queryRef
  );
  return <>{qRef != null ? <Billing queryRef={qRef} /> : null}</>;
}

type Props = {
  queryRef: PreloadedQuery<MerchantBilling_QueryType>;
};

function Billing({ queryRef }: Props) {
  const data = usePreloadedQuery<MerchantBilling_QueryType>(
    graphql`
      query MerchantBilling_Query(
        $accountID: ID
        $financesBefore: Time!
        $financesAfter: Time!
      ) {
        account(input: { accountID: $accountID }) {
          merchant {
            payments {
              stripeSetupStatus
              stripePayoutsEnabled
            }
            stripeBalance {
              available {
                value
                currency
              }
              pending {
                value
                currency
              }
            }
            ...MerchantFinances_merchant
          }
        }
      }
    `,
    queryRef
  );

  const dispatch = useToastDispatch();

  async function updateMerchantHandler() {
    try {
      const resp: mutationsUpdateMerchantMutationResponse = await makeMutation(
        UpdateMerchantPayments,
        {
          accountID: getCurrentAccount(),
        }
      );
      window.open(resp.updateMerchantPayments.updateLink, '_blank');
    } catch (err) {
      dispatch({
        text: `Unable to fetch update link`,
        alert: 'error',
        stayFor: 3000,
      });
      console.log('Unable to get update link', err);
    }
  }

  let accountReady =
    data.account.merchant?.payments?.stripeSetupStatus === 'ONBOARDED' &&
    data.account.merchant?.payments?.stripePayoutsEnabled;

  let nextPayoutDate = moment().date(20);

  if (nextPayoutDate.isBefore(moment())) {
    nextPayoutDate = nextPayoutDate.add(1, 'month');
  }

  const payoutDate = nextPayoutDate.format('Do MMMM YYYY');

  return (
    <Container>
      <Spacer spacing={4} vertical />
      <PageTitle>Billing</PageTitle>
      <Spacer vertical spacing={3} />
      <PageSection
        title='Balances'
        subtitle="Your users' subscriptions are paid into your balance at the end of their billing cycle."
      >
        <Spacer vertical spacing={4} />
        <BalanceSection>
          <RowLabel
            title='Next Payout Amount'
            description='Current amount to payout.'
          >
            {(data?.account.merchant?.stripeBalance.available.value ?? 0) / 100}{' '}
            {data?.account.merchant?.stripeBalance.available.currency.toUpperCase()}
          </RowLabel>
          <Spacer vertical spacing={4} />
          <RowLabel
            title='Next Payout Date'
            description='Payouts will be made to your bank account'
          >
            {payoutDate}
          </RowLabel>

          <Spacer vertical spacing={4} />
          <RowLabel
            title='Pending Balance'
            description='Will be available soon'
          >
            {(data?.account.merchant?.stripeBalance.pending.value ?? 0) / 100}{' '}
            {data?.account.merchant?.stripeBalance.pending.currency.toUpperCase()}
          </RowLabel>
        </BalanceSection>
      </PageSection>
      {/* <Spacer vertical spacing={6} />
      <PageSection
        title='Finances'
        subtitle='Revenue and payment information'
        minHeight={'276px'}
      >
        {!!data.account.merchant && (
          <React.Suspense fallback={<Loader loading={true} />}>
            <MerchantFinances merchant={data.account.merchant} />
          </React.Suspense>
        )}
      </PageSection> */}
      <Spacer vertical spacing={6} />
      {accountReady ? (
        <PageSection
          title='Merchant payout account'
          subtitle='Your next payout amount is withdrawn to your payout account each month.'
          extraItems={
            <FlatButton
              darkenOnHover
              color='#5C6EF8'
              onClick={updateMerchantHandler}
            >
              View Payout Account
            </FlatButton>
          }
        >
          <InfoLabel label='ACCOUNT STATUS'>
            {data.account.merchant?.payments?.stripeSetupStatus}
          </InfoLabel>
          <Spacer vertical spacing={6} />
        </PageSection>
      ) : (
        <PageSection
          title='Merchant payout account'
          subtitle='Your account balance is withdrawn to your payout account each month.'
          extraItems={
            <FlatButton
              darkenOnHover
              color='#5C6EF8'
              onClick={async () => {
                try {
                  const resp: any = await makeMutation(OnboardMerchant, {
                    accountID: getCurrentAccount(),
                  });
                  window.open(resp.onboardMerchant.stripeOnboardURL, '_blank');
                } catch (err) {
                  dispatch({
                    text: `Unable to fetch setup account link`,
                    alert: 'error',
                    stayFor: 3000,
                  });
                  console.log('Unable to onboard', err);
                }
              }}
            >
              Setup payout account
            </FlatButton>
          }
        >
          <HelpText type='warning'>
            {data.account.merchant?.payments?.stripeSetupStatus === 'ONBOARDED'
              ? 'Finish setting up your merchant account to take payments'
              : 'Setup your payout account to recieve payments'}
          </HelpText>
          <Spacer vertical spacing={6} />
        </PageSection>
      )}
    </Container>
  );
}
